import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Reviewable } from '@/types'
import { nextPending, remainingFrom } from './reviewQueue'

export interface ReviewQueue<T> {
  active: boolean
  /** The item on screen. null when inactive or done. */
  current: T | null
  /** Active and nothing pending is left. */
  done: boolean
  remaining: number
  start: () => void
  /** Move past the shown item, whether it was decided or skipped. */
  advance: () => void
  stop: () => void
}

/**
 * One-at-a-time review over `rows` (APP-140). The order is fixed when the
 * queue starts: pending ids in `rows`' order. A skipped item is not offered
 * again in that run; a decided one is no longer pending, so it never is.
 *
 * The shown item is held by position, not recomputed from status, so the
 * optimistic verdict does not swap the form under the spinner.
 */
export function useReviewQueue<T extends Reviewable>(rows: T[]): ReviewQueue<T> {
  const [queue, setQueue] = useState<string[] | null>(null)
  const [position, setPosition] = useState(-1)

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows])
  const current = queue && position >= 0 ? (byId.get(queue[position]!) ?? null) : null

  const start = useCallback(() => {
    const ids = rows.filter((r) => r.status === 'pending').map((r) => r.id)
    setQueue(ids)
    setPosition(nextPending(ids, 0, byId))
  }, [rows, byId])

  const advance = useCallback(() => {
    if (queue) setPosition(nextPending(queue, position + 1, byId))
  }, [queue, position, byId])

  const stop = useCallback(() => {
    setQueue(null)
    setPosition(-1)
  }, [])

  // The shown item was deleted underneath the queue.
  const vanished = queue !== null && position >= 0 && current === null
  useEffect(() => {
    if (vanished) advance()
  }, [vanished, advance])

  return {
    active: queue !== null,
    current,
    done: queue !== null && position < 0,
    remaining: queue ? remainingFrom(queue, position, byId) : 0,
    start,
    advance,
    stop,
  }
}
